import React, { useState, useEffect, useMemo, useRef } from 'react';
import Editor, { type OnMount } from '@monaco-editor/react';
import { useQueryClient } from '@tanstack/react-query';
import { FiZap } from 'react-icons/fi';
import { toast } from 'sonner';
import { Icon } from '@/shared/icons';
import { Tooltip } from '@/shared/components/ui';
import { PromptMigrationWizard } from './migration/PromptMigrationWizard';
import { detectPromptSections, type PromptSection } from '../utils/prompt-sections';
import type { CreateAIAgentRequest } from '../services/agentsApi';

/** Instância do editor e API do Monaco, tipadas a partir do próprio wrapper. */
type CodeEditor = Parameters<OnMount>[0];
type MonacoApi = Parameters<OnMount>[1];

/** Quanto tempo o realce da linha de destino fica visível (casa com a animação em index.css). */
const SECTION_FLASH_MS = 1100;

interface BehaviorTabProps {
  formData: Partial<CreateAIAgentRequest>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<CreateAIAgentRequest>>>;
  isEditMode: boolean;
  agentId?: string;
  hasPromptChanged: boolean;
  /**
   * Opt-out do teste pré-deploy automático. Quando `true`, o save pula
   * o `PreDeployTestModal` e envia `X-Override-Reason` ao backend pra
   * driblar o `DeployGate`. Default `false` (teste roda).
   */
  skipPreDeployTest: boolean;
  onSkipPreDeployTestChange: (checked: boolean) => void;
}

export const BehaviorTab: React.FC<BehaviorTabProps> = ({
  formData,
  setFormData,
  isEditMode,
  agentId,
  hasPromptChanged,
  skipPreDeployTest,
  onSkipPreDeployTestChange,
}) => {
  const queryClient = useQueryClient();
  const [showMigrationWizard, setShowMigrationWizard] = useState(false);

  // Estado para altura dinâmica do editor
  const [editorHeight, setEditorHeight] = useState<number>(600);

  // Calcula altura do editor baseada no tamanho da tela
  useEffect(() => {
    const calculateEditorHeight = () => {
      const screenHeight = window.innerHeight;
      // Buffer vertical reservado pra UI ao redor (header da página +
      // padding do content + header/footer do próprio editor). Calibrado
      // em 2026-05-13: 360 (sobrava ~10%) → 240 (passou) → 280 (cabe).
      const calculatedHeight = Math.max(400, screenHeight - 290);
      setEditorHeight(calculatedHeight);
    };

    // Calcula na montagem
    calculateEditorHeight();

    // Recalcula quando a janela é redimensionada
    window.addEventListener('resize', calculateEditorHeight);

    return () => {
      window.removeEventListener('resize', calculateEditorHeight);
    };
  }, []);

  // Seções XML detectadas no prompt (tags de topo) — exibidas no rodapé do editor
  // (padrão .mon-tags do mockup) e clicáveis: cada uma é âncora pro editor.
  const detectedSections = useMemo(
    () => detectPromptSections(formData.systemPrompt || ''),
    [formData.systemPrompt],
  );

  // Instância do editor + API do Monaco, capturadas no mount — é o que permite
  // rolar até uma seção. Antes das âncoras ninguém guardava essa referência.
  const editorRef = useRef<CodeEditor | null>(null);
  const monacoRef = useRef<MonacoApi | null>(null);
  /** Última seção visitada, pra clicar de novo na mesma tag avançar pra próxima ocorrência. */
  const lastJumpRef = useRef<{ name: string; index: number } | null>(null);
  const flashRef = useRef<ReturnType<CodeEditor['createDecorationsCollection']> | null>(null);
  const flashTimeoutRef = useRef<number | null>(null);

  /**
   * O Monaco vem por loader assíncrono, mas o rodapé de seções nasce do
   * `formData` e aparece antes dele. Sem esse estado as âncoras ficam com
   * cara de link durante o "Loading..." e o clique não faz nada.
   */
  const [isEditorReady, setIsEditorReady] = useState(false);

  const handleEditorMount: OnMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    setIsEditorReady(true);
  };

  /** Limpa o realce e o timer pendente (troca de seção, desmontagem). */
  const clearSectionFlash = () => {
    if (flashTimeoutRef.current !== null) {
      window.clearTimeout(flashTimeoutRef.current);
      flashTimeoutRef.current = null;
    }
    flashRef.current?.clear();
    flashRef.current = null;
  };

  // Timer do realce não pode sobreviver ao desmontar a aba.
  useEffect(() => clearSectionFlash, []);

  /**
   * Leva o editor até a seção clicada.
   *
   * A posição vem do modelo do Monaco (`getPositionAt`), não de contagem de
   * `\n` no state — assim continua certa mesmo se o texto do editor estiver
   * um render à frente do `formData`.
   */
  const jumpToSection = (section: PromptSection) => {
    const editor = editorRef.current;
    const monaco = monacoRef.current;
    const model = editor?.getModel();
    if (!editor || !monaco || !model) return;

    // Clique repetido na mesma seção percorre as ocorrências e volta ao início.
    const previous = lastJumpRef.current;
    const occurrence =
      previous && previous.name === section.name
        ? (previous.index + 1) % section.offsets.length
        : 0;
    const offset = section.offsets[occurrence];
    if (offset === undefined) return;
    lastJumpRef.current = { name: section.name, index: occurrence };

    // Offset pode ter ficado além do texto atual (prompt encolheu entre o
    // render e o clique) — `getPositionAt` satura no fim, então é seguro.
    const position = model.getPositionAt(offset);
    editor.setPosition(position);
    editor.revealLineInCenter(position.lineNumber);
    editor.focus();

    // Realce da linha de destino, que apaga sozinho (animação em index.css).
    clearSectionFlash();
    flashRef.current = editor.createDecorationsCollection([
      {
        range: new monaco.Range(position.lineNumber, 1, position.lineNumber, 1),
        options: { isWholeLine: true, className: 'prompt-section-flash' },
      },
    ]);
    flashTimeoutRef.current = window.setTimeout(clearSectionFlash, SECTION_FLASH_MS);
  };

  return (
    <div className="w-full">
      {/* System Prompt com Monaco Editor — ocupa toda a largura disponível.
          A VersionsSidebar foi removida em 2026-05-13 porque a aba
          "Versões" já mostra o histórico completo, evitando duplicação. */}
      <div className="space-y-6"><div
        className="rounded-xl overflow-hidden"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          border: '1px solid var(--theme-card-border)',
        }}
      >
        {/* Header de seção (padrão card-de-seção do mockup agent-form: tile + título + sub DENTRO do card) */}
        <div className="flex items-center gap-3 p-4 flex-wrap" style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
          <span
            className="flex items-center justify-center flex-none rounded-[10px]"
            style={{ width: 34, height: 34, backgroundColor: 'rgba(148,163,184,0.14)', color: 'var(--theme-muted-text)' }}
          >
            <Icon name="message-square" size={16} />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-[15px] font-semibold flex items-center gap-2 flex-wrap" style={{ color: 'var(--theme-card-text)' }}>
              Comportamento do Agente
              {hasPromptChanged && (
                <span
                  className="px-2 py-0.5 rounded text-xs font-medium"
                  style={{ backgroundColor: '#fef3c7', color: '#d97706' }}
                >
                  Alterações não salvas
                </span>
              )}
            </h3>
            <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
              System Prompt em XML — persona, regras e escopo
            </p>
          </div>
        </div>
        <div>
          {/* Opt-out do teste pré-deploy — primeiro item do corpo do card,
              acima do editor (padrão .chk do mockup agent-form). Por default
              o save dispara o teste quando o prompt/modelo muda; marcar pula
              o teste mas exige justificativa via X-Override-Reason. */}
          {isEditMode && (
            <label
              className="flex items-center gap-[10px] cursor-pointer select-none px-4"
              style={{ paddingTop: 7, paddingBottom: 7 }}
              title="O teste pré-deploy roda automaticamente quando você muda o prompt ou modelo. Marque para pular — você precisará justificar a publicação direta."
            >
              <input
                type="checkbox"
                checked={skipPreDeployTest}
                onChange={(e) => {
                  const next = e.target.checked;
                  onSkipPreDeployTestChange(next);
                  localStorage.setItem(
                    'skip-pre-deploy-test',
                    String(next),
                  );
                }}
                className="sr-only"
                data-testid="agent-form-skip-pre-deploy-checkbox"
              />
              <span
                className="flex items-center justify-center flex-none rounded-md"
                style={{
                  width: 20,
                  height: 20,
                  border: skipPreDeployTest
                    ? '1.5px solid var(--theme-primary)'
                    : '1.5px solid var(--theme-input-border)',
                  backgroundColor: skipPreDeployTest ? 'var(--theme-primary)' : 'transparent',
                  color: skipPreDeployTest ? '#ffffff' : 'transparent',
                }}
              >
                <Icon name="check" size={14} />
              </span>
              <span className="text-sm" style={{ color: 'var(--theme-card-text)' }}>
                Pular teste pré-deploy nesta alteração
              </span>
            </label>
          )}
          {/* Header do Editor */}
          <div
            className="flex items-center justify-between px-4 py-2 border-b"
            style={{ borderColor: 'var(--theme-card-border)' }}
          >
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium" style={{ color: 'var(--theme-muted-text)' }}>
                System Prompt *
              </span>
              <span
                className="px-2 py-0.5 rounded text-xs"
                style={{ backgroundColor: 'rgba(148,163,184,0.14)', color: 'var(--theme-muted-text)' }}
              >
                XML
              </span>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowMigrationWizard(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '4px 12px',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0',
                  background: '#fff',
                  color: '#8b5cf6',
                  fontSize: '12px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                <FiZap size={14} />
                Otimizar Prompt
              </button>
            </div>
          </div>

          {/* Monaco Editor */}
          <Editor
            height={`${editorHeight}px`}
            language="xml"
            value={formData.systemPrompt || ''}
            onChange={(value) => setFormData({ ...formData, systemPrompt: value || '' })}
            onMount={handleEditorMount}
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              lineHeight: 22,
              fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
              wordWrap: 'on',
              lineNumbers: 'on',
              renderLineHighlight: 'line',
              scrollBeyondLastLine: false,
              automaticLayout: true,
              padding: { top: 16, bottom: 16 },
              scrollbar: {
                vertical: 'auto',
                horizontal: 'hidden',
                verticalScrollbarSize: 10,
              },
              bracketPairColorization: { enabled: true },
              guides: {
                bracketPairs: true,
                indentation: true,
              },
            }}
            theme="vs-dark"
          />

          {/* Footer do Editor — contagem à esquerda, seções XML detectadas à direita (mockup .mon-foot) */}
          <div
            className="flex items-center justify-between gap-[10px] flex-wrap px-4 py-2 border-t text-xs"
            style={{ borderColor: 'var(--theme-card-border)', color: 'var(--theme-muted-text)' }}
          >
            <span>
              {formData.systemPrompt?.split('\n').length || 0} linhas • {formData.systemPrompt?.length || 0} caracteres
            </span>
            {detectedSections.length > 0 && (
              <span
                className="flex items-center gap-1 flex-wrap"
                style={{ opacity: 0.7 }}
                data-testid="agent-form-section-anchors"
              >
                <span>seções:</span>
                {detectedSections.map((section, index) => (
                  <React.Fragment key={section.name}>
                    {index > 0 && <span aria-hidden="true">·</span>}
                    <Tooltip
                      content={
                        !isEditorReady
                          ? 'Carregando o editor...'
                          : section.offsets.length > 1
                            ? `${section.offsets.length} ocorrências — clique para percorrer`
                            : `Ir até <${section.name}> no editor`
                      }
                    >
                      <button
                        type="button"
                        className="prompt-section-anchor"
                        disabled={!isEditorReady}
                        onClick={() => jumpToSection(section)}
                        data-testid={`agent-form-section-anchor-${section.name}`}
                      >
                        {section.name}
                      </button>
                    </Tooltip>
                  </React.Fragment>
                ))}
              </span>
            )}
          </div>
        </div>
      </div></div>
      {showMigrationWizard && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.5)',
          }}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '12px',
              width: '560px',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
              '--theme-text': '#1e293b',
              '--theme-muted-text': '#6b7280',
              '--theme-input-background': '#ffffff',
              '--theme-input-text': '#1e293b',
              '--theme-input-border': 'rgba(0, 0, 0, 0.10)',
              '--theme-card-background': '#ffffff',
              '--theme-card-text': '#334155',
              '--theme-card-border': 'rgba(0, 0, 0, 0.06)',
            } as React.CSSProperties}
          >
            <PromptMigrationWizard
              agentId={agentId}
              systemPrompt={formData.systemPrompt || ''}
              databaseIds={formData.databases || []}
              onComplete={(updatedPrompt) => {
                // Backend (executeMigration) já persistiu o systemPrompt
                // no agente após cada bloco aceito. Aqui só sincronizamos
                // o state local + invalidamos a query do agente pra
                // refetch a versão fresh (que vai re-popular formData via
                // useEffect do AgentFormPage, deixando hasPromptChanged
                // = false). Toast confirma ao admin que está tudo salvo.
                setFormData((prev) => ({ ...prev, systemPrompt: updatedPrompt }));
                if (agentId) {
                  void queryClient.invalidateQueries({
                    queryKey: ['ai-agent', agentId],
                  });
                  void queryClient.invalidateQueries({
                    queryKey: ['ai-agents'],
                  });
                  toast.success(
                    'Prompt otimizado e salvo. Conteúdo factual migrado pra base de conhecimento.',
                  );
                }
                setShowMigrationWizard(false);
              }}
              onCancel={() => setShowMigrationWizard(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};
